import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { Plus, RefreshCw, AlertTriangle, Container } from 'lucide-react'
import { toast } from 'sonner'
import ContainerTypesTable from './ContainerTypesTable'
import ContainerTypeForm from './ContainerTypeForm'
import { useContainerTypes, useCreateContainerType, useUpdateContainerType, useDeleteContainerType, useRestIntegrations, ContainerType, CreateContainerTypeRequest, UpdateContainerTypeRequest, Integration } from '@wove/api'

interface ContainerTypesProps {
  integrationId?: string
}

export default function ContainerTypes({ integrationId: providedIntegrationId }: ContainerTypesProps) {
  const { data: integrations = [], isLoading: integrationsLoading, error: integrationsError } = useRestIntegrations()
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('')

  // Use provided integration ID or manage selection locally
  const effectiveIntegrationId = providedIntegrationId || selectedIntegrationId

  // Set default integration when data loads (only if no integration provided)
  React.useEffect(() => {
    if (!providedIntegrationId && integrations.length > 0 && !selectedIntegrationId) {
      setSelectedIntegrationId(integrations[0].id)
    }
  }, [integrations, selectedIntegrationId, providedIntegrationId])

  const [showForm, setShowForm] = useState(false)
  const [editingContainerType, setEditingContainerType] = useState<ContainerType | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<ContainerType | null>(null)

  const {
    data: containerTypes = [],
    isLoading,
    error,
    refetch,
  } = useContainerTypes(effectiveIntegrationId)

  const createContainerTypeMutation = useCreateContainerType(effectiveIntegrationId)
  const updateContainerTypeMutation = useUpdateContainerType(effectiveIntegrationId)
  const deleteContainerTypeMutation = useDeleteContainerType(effectiveIntegrationId)

  const handleCreateContainerType = async (containerTypeData: CreateContainerTypeRequest) => {
    try {
      await createContainerTypeMutation.mutateAsync(containerTypeData)
      setShowForm(false)
      toast.success('Container type created successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to create container type')
    }
  }

  const handleUpdateContainerType = async (containerTypeData: UpdateContainerTypeRequest) => {
    if (!editingContainerType) return
    try {
      await updateContainerTypeMutation.mutateAsync({ woveCode: editingContainerType.woveCode, data: containerTypeData })
      setEditingContainerType(null)
      setShowForm(false)
      toast.success('Container type updated successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to update container type')
    }
  }

  const handleDeleteContainerType = async (containerType: ContainerType) => {
    try {
      await deleteContainerTypeMutation.mutateAsync(containerType.woveCode)
      setShowDeleteConfirm(null)
      toast.success('Container type deleted successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete container type')
    }
  }

  const openCreateForm = () => {
    setEditingContainerType(null)
    setShowForm(true)
  }

  const openEditForm = (containerType: ContainerType) => {
    setEditingContainerType(containerType)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingContainerType(null)
  }

  // Handle integrations loading/error states (only if no integration provided)
  if (!providedIntegrationId) {
    if (integrationsError) {
      return (
        <div className="space-y-6" >
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Container Types</h2>
            <p className="text-muted-foreground">
              Configure container type mappings for your integrations
            </p>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading integrations
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{integrationsError.message}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (integrationsLoading) {
      return (
        <div className="space-y-6" >
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Container Types</h2>
            <p className="text-muted-foreground">
              Configure container type mappings for your integrations
            </p>
          </div>
          
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading integrations...</span>
          </div>
        </div>
      )
    }

    if (integrations.length === 0) {
      return (
        <div className="space-y-6" >
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Container Types</h2>
            <p className="text-muted-foreground">
              Configure container type mappings for your integrations
            </p>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No integrations found
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>You need to set up an integration before you can configure container types.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  if (error) {
    return (
      <div className="space-y-6" >
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Container Types</h2>
          <p className="text-muted-foreground">
            Configure container type mappings for your integrations
          </p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading container types
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6" >
      {!providedIntegrationId && (
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Container Types</h2>
          <p className="text-muted-foreground">
            Map container types between Wove and your integration system
          </p>
        </div>
      )}

      {/* Integration Selector - only show when no integration provided */}
      {!providedIntegrationId && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <Container className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">
                Integration Configuration
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Container types are configured per integration. 
                {integrations.length === 1 
                  ? ` Currently managing container types for: ${integrations[0].integrationSource || integrations[0].integrationType}`
                  : ' Select an integration to manage its container types.'
                }
              </p>
              {integrations.length > 1 && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    Select Integration:
                  </label>
                  <select
                    value={selectedIntegrationId}
                    onChange={(e) => setSelectedIntegrationId(e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-64"
                  >
                    {integrations.map((integration: Integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.integrationSource || integration.integrationType}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {integrations.length === 1 && (
                <div className="mt-2 text-xs text-blue-600">
                  Integration ID: {selectedIntegrationId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium">Container Type Mappings</h3>
          <span className="text-sm text-gray-500">
            {containerTypes.length} mapping{containerTypes.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={openCreateForm}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Container Type
          </Button>
        </div>
      </div>

      {/* Container Types Table */}
      <ContainerTypesTable
        containerTypes={containerTypes}
        onEdit={openEditForm}
        onDelete={(containerType) => setShowDeleteConfirm(containerType)}
        isLoading={isLoading}
      />

      {/* Create/Edit Form Modal */}
      {showForm && (
        <ContainerTypeForm
          containerType={editingContainerType || undefined}
          onSubmit={(data) => {
            if (editingContainerType) {
              handleUpdateContainerType(data as UpdateContainerTypeRequest)
            } else {
              handleCreateContainerType(data as CreateContainerTypeRequest)
            }
          }}
          onCancel={closeForm}
          isSubmitting={createContainerTypeMutation.isPending || updateContainerTypeMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Container Type
                </h3>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete the container type mapping for "{showDeleteConfirm.woveCode}"? 
                  This action cannot be undone.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={deleteContainerTypeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteContainerType(showDeleteConfirm)}
                  disabled={deleteContainerTypeMutation.isPending}
                >
                  {deleteContainerTypeMutation.isPending ? 'Deleting...' : 'Delete Container Type'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}